import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getStatusCategory } from '@/utils/statusCategoryMap';

export interface TemplateField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'select';
  options?: string[];
}

export interface MilestoneTemplate {
  id: string;
  sales_item_id: string | null;
  metadata: {
    fields?: TemplateField[];
    status_flow?: string[];
    /** Etapa não obrigatória: não bloqueia a conclusão do processo */
    optional?: boolean;
  } | null;
  is_per_person: boolean | null;
  icon: string | null;
  estimated_duration_days: number | null;
}

export interface MilestoneContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

export interface CaseMilestone {
  id: string;
  case_id: string;
  milestone_template_id: string | null;
  name: string;
  description: string | null;
  milestone_order: number;
  current_status: string | null;
  status_category: string | null;
  display_order: number | null;
  data: Record<string, any>;
  contact_id: string | null;
  started_date: string | null;
  completed_date: string | null;
  completed_by_event_id: string | null;
  completed_by_task_id: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  // JOINed
  template?: MilestoneTemplate | null;
  contact?: MilestoneContact | null;
}

/** Etapas marcadas como opcionais não bloqueiam a conclusão do processo. */
export function isOptionalMilestone(m: CaseMilestone): boolean {
  return m.template?.metadata?.optional === true;
}

export function useCaseMilestones(caseId: string | null) {
  const queryClient = useQueryClient();

  const { data: milestones = [], isLoading, error, refetch } = useQuery({
    queryKey: ['case-milestones', caseId],
    queryFn: async () => {
      if (!caseId) return [];

      const { data, error } = await supabase
        .from('case_milestones')
        .select(`
          *,
          template:milestone_templates(
            id, sales_item_id, metadata, is_per_person, icon, estimated_duration_days
          ),
          contact:contacts!case_milestones_contact_id_fkey(
            id, first_name, last_name
          )
        `)
        .eq('case_id', caseId)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      return (data ?? []) as unknown as CaseMilestone[];
    },
    enabled: !!caseId,
  });

  // Separate case-level vs per-person milestones
  const caseStages = milestones.filter(m => !m.contact_id);
  const personStages = milestones.filter(m => !!m.contact_id);

  const personStagesByContact = personStages.reduce((acc, m) => {
    const key = m.contact_id!;
    if (!acc[key]) {
      acc[key] = { contact: m.contact ?? null, milestones: [] };
    }
    acc[key].milestones.push(m);
    return acc;
  }, {} as Record<string, { contact: MilestoneContact | null; milestones: CaseMilestone[] }>);

  const updateMilestoneMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      // Auto-derive status_category if current_status is being changed
      const finalUpdates = { ...updates };
      if (finalUpdates.current_status && !finalUpdates.status_category) {
        finalUpdates.status_category = getStatusCategory(finalUpdates.current_status);
      }
      finalUpdates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('case_milestones')
        .update(finalUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-milestones', caseId] });
    },
  });

  const toggleMilestoneComplete = async (milestone: CaseMilestone) => {
    const isCompleting = !milestone.completed_date;
    await updateMilestoneMutation.mutateAsync({
      id: milestone.id,
      updates: {
        current_status: isCompleting ? 'completed' : 'pending',
        status_category: isCompleting ? 'completed' : 'not_started',
        completed_date: isCompleting ? new Date().toISOString().split('T')[0] : null,
      },
    });
  };

  // Calculate progress (etapas opcionais não entram no cálculo)
  const requiredMilestones = milestones.filter(m => !isOptionalMilestone(m));
  const completedCount = requiredMilestones.filter(
    m =>
      m.current_status === 'completed' ||
      m.current_status === 'not_applicable' ||
      m.status_category === 'completed' ||
      m.completed_date
  ).length;
  const totalCount = requiredMilestones.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const instantiateMutation = useMutation({
    mutationFn: async () => {
      if (!caseId) throw new Error('Case ID is required');

      // Fetch catalog_id: cases → items → catalog
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .select('item_id, items:items!inner(catalog_id)')
        .eq('id', caseId)
        .single();

      if (caseError || !caseData) throw caseError ?? new Error('Case not found');

      const catalogId = (caseData as any).items?.catalog_id;
      if (!catalogId) throw new Error('Catalog not found for this case');

      const { error: rpcError } = await supabase.rpc('instantiate_case_milestones', {
        p_case_id: caseId,
        p_catalog_id: catalogId,
      });

      if (rpcError) throw rpcError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-milestones', caseId] });
    },
  });

  return {
    milestones,
    caseStages,
    personStages,
    personStagesByContact,
    isLoading,
    error,
    refetch,
    updateMilestone: updateMilestoneMutation.mutateAsync,
    toggleMilestoneComplete,
    isUpdating: updateMilestoneMutation.isPending,
    instantiateMilestones: instantiateMutation.mutateAsync,
    isInstantiating: instantiateMutation.isPending,
    completedCount,
    totalCount,
    progressPercentage,
  };
}
